/** @format */

import React, { useEffect, useState } from 'react';
import { AppBar, Card, CardActions, CardContent, Tab } from '@mui/material';
import { ButtonLoading, LoadingPage, Icons } from 'components';
import { useForm, FormProvider } from 'react-hook-form';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { findIndex } from 'lodash';
import { getClientService } from 'components/ClientIdCombo/services/ClientIdComboServices';
import businessListServices from 'components/BusinessList/services/BusinessListServices';
import isEqual from 'lodash/isEqual';
import clone from 'lodash/clone';
import createToast from 'components/Toast/Toast';

import {
  CalculationMethod,
  Beneficiary,
  GeneralInfo,
  HeaderParameters,
  BodyParameters,
} from '../DefineWebService/components';
import {
  serviceCallListApi,
  serviceCallEditApi,
  serviceSetRelatedServiceApi,
} from '../services/WebServiceServices';
import ErrorHandlingBlocks from '../ErrorHandlingBlocks';
import { useStyles } from './Style';

const EditWebService = (props) => {
  const classes = useStyles();
  const { id } = props.match.params; // id web service (entity ID)

  const methods = useForm();

  const { handleSubmit, reset, formState: { errors } } = methods;
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);

  const [expanded, setExpanded] = useState('panel-1');
  const [selected, setSelected] = useState({});
  const [originalData, setOriginalData] = useState({});

  const [openSnack, setOpenSnack] = useState(false);

  const selectChange = (name, e) => {
    setSelected((prevState) => {
      return { ...prevState, [name]: e.target.value };
    });
  };

  let increaseVersion = false;

  const typeOfBody = {
    optional: {
      scHiddenParameter: false,
      scIsRequired: false,
    },
    required: {
      scHiddenParameter: false,
      scIsRequired: true,
    },
    hidden: {
      scHiddenParameter: true,
      scIsRequired: false,
    },
  };

  const generateBodyData = (data) => {
    if (Array.isArray(data)) {
      return data.map(generateBodyData);
    }
    data = { ...data, ...typeOfBody[data.typeOfBody] };

    if (data.typeOfBody === 'required') {
      data.scParameterDefaultValue = null;
    }

    delete data.typeOfBody;
    if (data?.scParameters) {
      data = {
        ...data,
        scParameters: [...generateBodyData(data.scParameters)],
      };
    }
    return data;
  };

  const onSubmit = (data) => {
    // چک کردن نیاز به تغییر ورژن سرویس
    if (data.currentVersion === originalData.currentVersion) {
      const objCheckVersion = {
        scHttpVerb: data.scHttpVerb,
        scUrl: data.scUrl,
        price: data.price,
        scPriceCalculationType: data.scPriceCalculationType,
        scAutoApprove: data.scAutoApprove === 'true',
      };

      if (!isEqual(objCheckVersion, originalData.checkVersion)) {
        alert(
          'با تغییر هر یک از پارامترهای متد، محاسبه، دسترسی، قیمت و آدرس وب سرویس نیاز به تغییر ورژن می باشد.',
        );
        return;
      }
    } else {
      increaseVersion = true;
    }

    let dataBody = [];
    if (data?.scInputParams) {
      dataBody = generateBodyData(data.scInputParams);
    }

    const metaDataObj = {
      scGuildCode: typeof data.scGuildCode === 'object' ? data.scGuildCode.code : data.scGuildCode,
      scNamespace: data.scNamespace ? data.scNamespace : null, // *  فضای نام در این بخش وارد میشود که معمولا همان اکشن میباشد
      scUrl: data.scUrl,
      scMethodName: data.scMethodName ? data.scMethodName : null, // soap method
      scInputParams: dataBody.length ? dataBody : null, // body  ----- JSON.stringify(data.serviceParams);
      scHeaderParams: data.scHeaderParams ? data.scHeaderParams : [],
      scType: data.scType,
      scEnName: data.scEnName,
      scProvider: data.scProvider,
      scFaName: data.scFaName,
      scHttpVerb: data.scHttpVerb,
      scPriceCalculationType: data.scPriceCalculationType,
      scPriceHeader: data.scPriceHeader ? data.scPriceHeader : '',
      scPriceInquiryUrl: data.scPriceInquiryUrl ? data.scPriceInquiryUrl : '',
      scPaymentType: 'SUBSCRIPTIONPLAN', // data.scPaymentType
      scStakeholderList: data.scStakeholderList || [],
      scAutoApprove: false,
      scClientId: '',
      scSuccessfulResult: {
        scHttpStatusCode: data.scHttpStatusCode || '',
        scHttpResponse: data.scHttpResponse || '',
      },
    };

    if (data.scAutoApprove === 'true') {
      metaDataObj.scClientId =
        typeof data.scClientId === 'object' ? data.scClientId.client_id : data.scClientId;
      metaDataObj.scAutoApprove = true;
    }

    if (data.scStakeholderList) {
      metaDataObj.scStakeholderList = data.scStakeholderList.map((item) => ({
        ...item,
        businessId: item.businessId.id,
      }));
    }

    if (data.contentType) {
      metaDataObj.scHeaderParams.push({
        scParameterName: 'content-type',
        scParameterDesc: '',
        scParameterDefaultValue:
          typeof data.contentType === 'object' ? data.contentType.value : data.contentType,
        scIsRequired: 'false',
        scHiddenParameter: 'true',
        scParameterLogMode: 'LOGGABLE',
      });
    }

    const dataServiceCall = {
      preferredTaxRate: '0',
      price: data.price,
      enable: 'true',
      unlimited: 'true',
      serviceVersion: data.currentVersion,
      ...metaDataObj,
      description: data.description,
      status: 'pending',
      scProvider: data.scProvider,
    };

    if (data?.providerBusinessId) {
      dataServiceCall.providerBusinessId = data?.providerBusinessId.id;
    }

    dataServiceCall.productId = id; // entityId

    if (increaseVersion) {
      dataServiceCall.increaseVersion = true;
    }

    const dataApi = {
      dataServiceCall,
      dataServiceSet: [
        {
          webserviceId: id,
          serviceSetId: data.serviceSet.map((val) => String(val.id)),
        },
      ],
    };

    async function editServiceCall(params) {
      try {
        setLoading(true);

        await serviceCallEditApi(params);

        createToast('موفق', 'ویرایش سرویس با موفقیت انجام شد.', 'success');

        setOpenSnack(true);
        setLoading(false);
      } catch (error) {
        setOpenSnack(true);
        setLoading(false);
      }
    }

    editServiceCall(dataApi);
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingPage(true);
        const resWebService = await serviceCallListApi({ id });
        const resServiceSet = await serviceSetRelatedServiceApi(id);

        let clientIdSelected = {};

        let dataServiceCall = resWebService.result[0];

        let dataServiceSet = resServiceSet.result.rows.length ? resServiceSet.result.rows : [];
        if (dataServiceSet.length > 0) {
          dataServiceSet = dataServiceSet.map((item) => ({
            name: item.cols[1],
            id: item.cols[0],
          }));
        }

        if (dataServiceCall.scClientId && dataServiceCall.scAutoApprove === true) {
          const clientIDResult = await getClientService();
          const clientIDList = clientIDResult.result.clients;

          clientIdSelected = clientIDList.filter(
            (item) => item.client_id === dataServiceCall.scClientId,
          );

          clientIdSelected = {
            name: clientIdSelected[0].name,
            client_id: clientIdSelected[0].client_id,
          };
        }

        if (dataServiceCall?.scStakeholderList && dataServiceCall.scStakeholderList.length > 0) {
          const businessId = dataServiceCall.scStakeholderList
            .map((item) => item.businessId)
            .join(',');

          const resultB = await businessListServices.getBusinessList({
            businessId,
          });

          const list = resultB.result;

          const business = {};
          list.forEach((element) => {
            business[element.id] = element.name;
          });

          const stackSelected = dataServiceCall.scStakeholderList.map((item) => ({
            ...item,
            businessId: {
              name: business[item.businessId],
              id: item.businessId,
            },
          }));

          dataServiceCall.scStakeholderList = stackSelected;
        }

        let headers = [];
        let contentType = {};
        if (dataServiceCall?.scHeaderParams) {
          headers = clone(dataServiceCall.scHeaderParams);

          const indexContentType = findIndex(headers, {
            scParameterName: 'content-type',
            scParameterDesc: '',
            scIsRequired: false,
            scHiddenParameter: true,
            scParameterLogMode: 'LOGGABLE',
          });

          if (indexContentType > -1) {
            contentType = headers[indexContentType];

            headers.splice(indexContentType, 1);
          }
        }

        if (dataServiceCall?.scInputParams) {
          const data = dataServiceCall.scInputParams;

          const generateBodyData = (data) => {
            if (Array.isArray(data)) {
              return data.map(generateBodyData);
            }
            let typeOfBody = 'optional';
            if (!data.scHiddenParameter && !data.scIsRequired) {
              typeOfBody = 'optional';
            } else if (!data.scHiddenParameter && data.scIsRequired) {
              typeOfBody = 'required';
            } else if (data.scHiddenParameter && !data.scIsRequired) {
              typeOfBody = 'required';
            }

            data = { ...data, typeOfBody };

            if (data?.scParameters) {
              data = {
                ...data,
                scParameters: [...generateBodyData(data.scParameters)],
              };
            }
            return data;
          };

          dataServiceCall.scInputParams = generateBodyData(data);
        }

        if (dataServiceCall?.providerBusiness) {
          const providerBusinessId = {
            id: dataServiceCall?.providerBusiness.id,
            name: dataServiceCall?.providerBusiness.name,
          };

          dataServiceCall = {
            ...dataServiceCall,
            providerBusinessId,
          };
        }

        const webService = {
          ...dataServiceCall,
          scFaName: dataServiceCall.name,
          scType: dataServiceCall.scType.toUpperCase(),

          scAutoApprove: dataServiceCall.scAutoApprove ? 'true' : 'false',
          scHttpStatusCode: dataServiceCall?.scSuccessfulResult?.scHttpStatusCode,
          scHttpResponse: dataServiceCall?.scSuccessfulResult?.scHttpResponse,
          serviceSet: dataServiceSet,
          scHeaderParams: headers,
          currentVersion: dataServiceCall.serviceVersion,
          contentType: contentType?.scParameterDefaultValue,
          scGuildCode: {
            name: dataServiceCall.guild.name,
            code: dataServiceCall.guild.code,
          },
          // scInputParams: dataServiceCall.scInputParams,
        };

        if (webService?.scHttpVerb) {
          webService.scHttpVerb = webService.scHttpVerb.toUpperCase();
        }

        if (webService.scAutoApprove === 'true') {
          webService.scClientId = {
            client_id: clientIdSelected.client_id,
            name: clientIdSelected.name,
          };
        }

        reset({
          ...webService,
        });

        /*
            1- تغییر متد 
            2- روش محاسبه
            3- نوع دسترسی
            4- قیمت
            5- آدرس وب سرویس 
          */
        setOriginalData({
          currentVersion: dataServiceCall.serviceVersion,
          checkVersion: {
            scHttpVerb: dataServiceCall.scHttpVerb,
            scUrl: dataServiceCall.scUrl,
            price: dataServiceCall.price,
            scPriceCalculationType: dataServiceCall.scPriceCalculationType,
            scAutoApprove: !!dataServiceCall.scAutoApprove,
          },
        });

        setLoadingPage(false);
        return;
      } catch (error) {


        setOpenSnack(true);
      }
    })();
  }, [id, reset]);

  const openPanel = (panel) => {
    setExpanded(panel);
  };

  useEffect(() => {
    if (Object.keys(errors).length) {
      ErrorHandlingBlocks(errors, openPanel);
    }
  }, [errors]);

  return (
    <>
      {loadingPage ? (
        <LoadingPage />
      ) : (
        <div className={classes.root}>
          <Card>
            <TabContext value="1">
              <AppBar position="static">
                <TabList>
                  <Tab label="ویرایش وب سرویس" value="1" />
                </TabList>
              </AppBar>
              <TabPanel value="1">
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                    <CardContent>
                      {/* GeneralInfo */}
                      <Accordion
                        expanded={expanded === 'general'}
                        onChange={handleChange('general')}
                      >
                        <AccordionSummary
                          className={classes.accordionSummary}
                          expandIcon={<Icons icon='CaretDown' color='#303DBF' size='23' />}
                          aria-controls="panel1a-content"
                          id="panel-1-header"
                        >
                          <Typography className={classes.heading}>اطلاعات کلی وب سرویس</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <GeneralInfo
                            selected={selected}
                            selectChange={selectChange}
                            classes={classes}
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* BodyParameters */}
                      <Accordion expanded={expanded === 'body'} onChange={handleChange('body')}>
                        <AccordionSummary
                          className={classes.accordionSummary}
                          expandIcon={<Icons icon='CaretDown' color='#303DBF' size='23' />}
                          aria-controls="panel2a-content"
                          id="panel2a-header"
                        >
                          <Typography className={classes.heading}>پارامترهای body</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <BodyParameters selected={selected} selectChange={selectChange} />
                        </AccordionDetails>
                      </Accordion>

                      {/* HeaderParameters */}
                      <Accordion expanded={expanded === 'header'} onChange={handleChange('header')}>
                        <AccordionSummary
                          className={classes.accordionSummary}
                          expandIcon={<Icons icon='CaretDown' color='#303DBF' size='23' />}
                          aria-controls="panel2a-content"
                          id="panel-3-header"
                        >
                          <Typography className={classes.heading}>پارامترهای Header</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <HeaderParameters
                            selected={selected}
                            selectChange={selectChange}
                            classes={classes}
                          />
                        </AccordionDetails>
                      </Accordion>

                      {/* CalculationMethod */}
                      <Accordion
                        expanded={expanded === 'calculation'}
                        onChange={handleChange('calculation')}
                      >
                        <AccordionSummary
                          className={classes.accordionSummary}
                          expandIcon={<Icons icon='CaretDown' color='#303DBF' size='23' />}
                          aria-controls="panel2a-content"
                          id="panel-4-header"
                        >
                          <Typography className={classes.heading}>روش محاسبه</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CalculationMethod selected={selected} selectChange={selectChange} />
                        </AccordionDetails>
                      </Accordion>

                      {/* Beneficiary */}
                      <Accordion
                        expanded={expanded === 'beneficiary'}
                        onChange={handleChange('beneficiary')}
                      >
                        <AccordionSummary
                          className={classes.accordionSummary}
                          expandIcon={<Icons icon='CaretDown' color='#303DBF' size='23' />}
                          aria-controls="panel2a-content"
                          id="panel-2-header"
                        >
                          <Typography className={classes.heading}>ذینفعان</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Beneficiary
                            selected={selected}
                            selectChange={selectChange}
                            classes={classes}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <CardActions className={classes.CardActions}>
                        <ButtonLoading
                          variant="contained"
                          type="submit"
                          color="primary"
                          disabled={loading}
                          loading={loading}
                          name="ویرایش"
                        />
                      </CardActions>
                    </CardContent>
                  </form>
                </FormProvider>
              </TabPanel>
            </TabContext>
          </Card>
        </div>
      )}
    </>
  );
};

export default EditWebService;
